import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Calendar, ArrowLeft, Share2 } from "lucide-react";
import GuideDetailSkeleton from "./GuideDetailSkeleton";

const Guidedetails = () => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { guideId } = useParams();

  useEffect(() => {
    const fetchGuideDetails = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/buying-guide/get_by_id/${guideId}`
        );
        setDetails(response.data.guide);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching guide:", err);
        setError("Failed to load guide details. Please try again later.");
        setLoading(false);
      }
    };

    fetchGuideDetails();
  }, [guideId]);

  // Format date to readable format
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleGoBack = () => {
    window.history.back();
  };

  if (loading) {
    return <GuideDetailSkeleton />;
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="bg-red-100 p-6 rounded-lg max-w-md text-center">
          <p className="text-red-700">{error}</p>
          <button
            className="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded transition duration-300"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!details) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="bg-yellow-100 p-6 rounded-lg max-w-md text-center">
          <p className="text-yellow-700">Guide not found.</p>
          <button
            className="mt-4 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded transition duration-300"
            onClick={handleGoBack}
          >
            Back to Guides
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen pb-12">
      {/* Hero Section */}
      <div
        className="w-full bg-cover bg-center h-64 md:h-96 relative"
        style={{
          backgroundImage: `url(${details.titleImageUrl})`,
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-60">
          <div className="container mx-auto px-4 h-full flex items-center">
            <div className="max-w-4xl">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                {details.headerTitle}
              </h1>
              <div className="flex items-center text-gray-300 text-sm">
                <Calendar size={16} className="mr-2" />
                <span>{formatDate(details.createdAt)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <button
            onClick={handleGoBack}
            className="flex items-center text-gray-600 hover:text-orange-600 transition duration-300"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back to Guides
          </button>
        </div>

        {/* Description */}
        <div className="bg-white rounded-xl shadow-md p-6 md:p-8 mb-8">
          <p className="text-gray-700 text-lg leading-relaxed">
            {details.headerDescription}
          </p>
        </div>

        {/* Materials Section */}
        <h2 className="text-2xl font-bold text-gray-800 mb-6">
          Recommended Building Materials
        </h2>

        <div className="space-y-6">
          {details.content &&
            details.content.map((item, index) => (
              <div
                key={item._id}
                className="bg-white rounded-xl shadow-md overflow-hidden"
              >
                <div className="md:flex">
                  <div className="md:w-1/3 h-64 md:h-auto">
                    {item.image && item.image.startsWith("http") ? (
                      <img
                        src={item.image}
                        alt={item.title}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-500">
                          Image not available
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-6 md:w-2/3">
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold mr-3">
                        {index + 1}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">
                        {item.title}
                      </h3>
                    </div>
                    <p className="text-gray-600 mt-3">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default Guidedetails;
